import { Component, OnInit } from '@angular/core';
import { ToastrService } from 'ngx-toastr';
import { LessonExcelImportService } from '@app/services/lesson-excel-import.service';
import { extractApiErrorMessage } from '@app/utils/ai-error-prompt';

@Component({
  selector: 'app-lesson-excel-import',
  templateUrl: './lesson-excel-import.component.html',
  styleUrl: './lesson-excel-import.component.scss',
})
export class LessonExcelImportComponent implements OnInit {
  lessons: any[] = [];
  selectedLessonId: number | null = null;
  newTitle = '';
  newSubject = '';
  creating = false;
  uploading = false;
  checking = false;
  populating = false;
  uploadingZip = false;
  downloadingZip = false;
  selectedFileName = '';
  selectedZipName = '';
  errors: string[] = [];
  warnings: string[] = [];
  preview: any = null;
  checkOk: boolean | null = null;
  lastMessage = '';
  pendingZipBase64 = '';
  pendingZipFileName = '';
  zipConflicts: string[] = [];

  constructor(
    private svc: LessonExcelImportService,
    private toastr: ToastrService,
  ) {}

  ngOnInit(): void {
    this.reload();
  }

  reload(): void {
    this.svc.listLessons().subscribe({
      next: (rows) => {
        this.lessons = Array.isArray(rows) ? rows : [];
        if (
          this.selectedLessonId != null &&
          !this.lessons.some((l) => Number(l.lessonId) === Number(this.selectedLessonId))
        ) {
          this.selectedLessonId = null;
        }
      },
      error: (err) => {
        this.lessons = [];
        this.toastr.error(extractApiErrorMessage(err, 'Failed to list lessons'));
      },
    });
  }

  createLesson(): void {
    const title = this.newTitle.trim();
    if (!title) {
      this.toastr.warning('Enter a lesson title');
      return;
    }
    this.creating = true;
    this.svc.createLesson(title, this.newSubject.trim()).subscribe({
      next: (res) => {
        this.creating = false;
        this.toastr.success('Lesson created');
        this.newTitle = '';
        this.newSubject = '';
        this.selectedLessonId = Number(res?.lessonId) || null;
        this.reload();
      },
      error: (err) => {
        this.creating = false;
        this.toastr.error(extractApiErrorMessage(err, 'Create failed'));
      },
    });
  }

  onExcelSelected(ev: Event): void {
    const input = ev.target as HTMLInputElement;
    const file = input.files?.[0];
    input.value = '';
    if (!file || this.selectedLessonId == null) return;
    this.selectedFileName = file.name;
    this.uploading = true;
    this.errors = [];
    this.warnings = [];
    this.preview = null;
    this.checkOk = null;
    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = String(reader.result || '');
      const b64 = dataUrl.includes(',') ? dataUrl.split(',')[1] : dataUrl;
      this.svc.uploadExcel(Number(this.selectedLessonId), file.name, b64).subscribe({
        next: (data) => {
          this.uploading = false;
          this.errors = data?.errors || [];
          this.warnings = data?.warnings || [];
          this.preview = data?.preview || null;
          this.checkOk = data?.checkOk === true;
          this.lastMessage = this.checkOk ? 'Excel uploaded' : 'Excel uploaded but validation failed';
          if (this.checkOk) this.toastr.success(this.lastMessage);
          else this.toastr.warning(this.lastMessage);
          this.reload();
        },
        error: (err) => {
          this.uploading = false;
          this.toastr.error(extractApiErrorMessage(err, 'Upload failed'));
        },
      });
    };
    reader.onerror = () => {
      this.uploading = false;
      this.toastr.error('Could not read file');
    };
    reader.readAsDataURL(file);
  }

  checkExcel(): void {
    if (this.selectedLessonId == null) return;
    this.checking = true;
    this.svc.checkExcel(Number(this.selectedLessonId)).subscribe({
      next: (data) => {
        this.checking = false;
        this.errors = data?.errors || [];
        this.warnings = data?.warnings || [];
        this.preview = data?.preview || null;
        this.checkOk = data?.ok === true;
        this.lastMessage = this.checkOk ? 'Validation passed' : 'Validation failed';
        if (this.checkOk) this.toastr.success(this.lastMessage);
        else this.toastr.warning(this.lastMessage);
      },
      error: (err) => {
        this.checking = false;
        const body = err?.error;
        const msg = extractApiErrorMessage(err, 'Check failed');
        this.errors = body?.errors || [msg];
        this.checkOk = false;
        this.toastr.error(msg);
      },
    });
  }

  onZipSelected(ev: Event): void {
    const input = ev.target as HTMLInputElement;
    const file = input.files?.[0];
    input.value = '';
    if (!file || this.selectedLessonId == null) return;
    this.selectedZipName = file.name;
    this.zipConflicts = [];
    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = String(reader.result || '');
      const b64 = dataUrl.includes(',') ? dataUrl.split(',')[1] : dataUrl;
      this.pendingZipBase64 = b64;
      this.pendingZipFileName = file.name;
      this.sendZip(false);
    };
    reader.onerror = () => this.toastr.error('Could not read ZIP');
    reader.readAsDataURL(file);
  }

  private sendZip(overwrite: boolean): void {
    if (this.selectedLessonId == null || !this.pendingZipBase64) return;
    this.uploadingZip = true;
    this.svc
      .uploadImageZip(
        Number(this.selectedLessonId),
        this.pendingZipFileName,
        this.pendingZipBase64,
        overwrite,
      )
      .subscribe({
        next: (data) => {
          this.uploadingZip = false;
          this.zipConflicts = [];
          this.pendingZipBase64 = '';
          const n = data?.totalWritten ?? 0;
          const ow = (data?.overwritten || []).length;
          this.toastr.success(
            ow ? `ZIP extracted: ${n} file(s), ${ow} overwritten` : `ZIP extracted: ${n} file(s)`,
          );
          this.lastMessage = `ZIP → ${data?.target || 'lesson folder'}`;
        },
        error: (err) => {
          this.uploadingZip = false;
          const body = err?.error || {};
          if (body?.code === 'NEEDS_OVERWRITE' || err?.status === 409) {
            this.zipConflicts = body.conflicts || [];
            const count = body.conflictCount || this.zipConflicts.length;
            const ok = confirm(
              `${count} file(s) already exist in the lesson upload folder.\n\nOverwrite existing files?`,
            );
            if (ok) this.sendZip(true);
            else this.toastr.info('ZIP upload cancelled — existing files kept');
            return;
          }
          if (body?.code === 'INVALID_ZIP_LAYOUT') {
            const extra = (body.invalidPaths || []).slice(0, 5).join('\n');
            this.toastr.error((body.error || 'Invalid ZIP layout') + (extra ? `\n${extra}` : ''));
            return;
          }
          this.toastr.error(extractApiErrorMessage(err, 'ZIP upload failed'));
        },
      });
  }

  downloadImageZip(): void {
    if (this.selectedLessonId == null || this.downloadingZip) return;
    this.downloadingZip = true;
    this.svc.downloadImageZip(Number(this.selectedLessonId)).subscribe({
      next: (blob) => {
        this.downloadingZip = false;
        if (!blob || blob.size === 0) {
          this.toastr.error('Download was empty — no slide images on the server yet.');
          return;
        }
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `lesson_${this.selectedLessonId}_images.zip`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
        this.toastr.success('Slide images ZIP downloaded');
        this.lastMessage = 'Downloaded ZIP (layout: slideId/file)';
      },
      error: (err) => {
        this.downloadingZip = false;
        this.toastr.error(
          extractApiErrorMessage(
            err,
            'Download failed. Processing may not have completed — try again.',
          ),
        );
      },
    });
  }

  populate(): void {
    if (this.selectedLessonId == null) return;
    if (!confirm('Populate TOC and slides from the uploaded Excel? Existing TOC/slides for this lesson will be replaced.')) {
      return;
    }
    this.populating = true;
    this.svc.populateLesson(Number(this.selectedLessonId)).subscribe({
      next: (data) => {
        this.populating = false;
        this.warnings = data?.warnings || [];
        this.preview = data?.preview || this.preview;
        this.lastMessage = 'Lesson populated from Excel';
        this.toastr.success(
          `${this.lastMessage} (${data?.slideCount || 0} slides, ${data?.questionCount || 0} questions)`,
        );
        this.reload();
      },
      error: (err) => {
        this.populating = false;
        const body = err?.error;
        const msg = extractApiErrorMessage(err, 'Populate failed');
        this.errors = body?.errors?.length ? body.errors : [msg];
        this.checkOk = false;
        this.toastr.error(msg);
      },
    });
  }
}
